import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styles from './VideoAndMessage.module.css';
import {
  DefaultMeetingSession,
  LogLevel,
  MeetingSessionConfiguration,
  ConsoleLogger,
  DefaultDeviceController
} from 'amazon-chime-sdk-js';
import { Button, Form, CloseButton, Row, Col, Spinner, Modal } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { CallEndIcon, ExpandIcon, UserProfilePictureIcon, MinimizeIcon } from './icons/Icons';
import useAxios from '../services/AxiosConfig';
import { useWebSocket } from '../services/webSocket/useWebSocket';
import { setExpanded, setMinimized, setCurrentMeetingId } from '../slicers/CallContainerSlice';
import { showAlert} from './PerinHealthAlertSlice';

function VideoCallContainer({ audioOnly = false, onClose, data }) {
  const meetingSessionRef = useRef(null);
  const meetingIdRef = useRef(null);
  const meetingArnRef = useRef(null);
  const audioElementRef = useRef(null);
  const previewVideoRef = useRef(null);
  const [meetingData, setMeetingData] = useState(null);
  const [videoTiles, setVideoTiles] = useState([]);
  const [isMuted, setIsMuted] = useState(false);
  const [hideVideo, setHideVideo] = useState(false);
  const [isVideoPreviewReady, setIsVideoPreviewReady] = useState(false);
  const [inPreviewMode, setInPreviewMode] = useState(true);
  const [callStarted, setCallStarted] = useState(false); // Track when both parties have joined
  const userId = useSelector((state) => state.userInfo.id);
  const userFullName = useSelector((state) => state.userInfo.fullName);
  const axiosConfig = useSelector(state => state.axiosConfig);
  const dispatch = useDispatch();
  const {expanded, minimized, joiningStaffCall }= useSelector(state => state.callContainer);
  const [activeSpeakerId, setActiveSpeakerId] = useState(null);
  const [showLeaveModal, setShowLeaveModal] = useState(null);

  // Determine if we're waiting for the other participant to join
  // For video calls: check remote video tiles
  // For audio calls: check attendee presence
  const remoteTiles = videoTiles.filter(tile => !tile.localTile);
  const [hasRemoteAttendee, setHasRemoteAttendee] = useState(false);
  const [patientHasLeft, setPatientHasLeft] = useState(false);
  const hasRemoteAttendeeRef = useRef(false); // Track previous state to detect when patient leaves (audio)
  const hadRemoteTilesRef = useRef(false); // Track previous state to detect when patient leaves (video)
  
  // For video calls: waiting if no remote tiles
  // For audio calls: waiting if no remote attendee detected
  const isWaiting = !inPreviewMode && !callStarted && (audioOnly ? !hasRemoteAttendee : remoteTiles.length === 0);

  const localAttendeeIdRef = useRef(null);
  const [callDuration, setCallDuration] = useState(0);
  // initialize countdown for 60 seconds
  const [timeoutDuration, setTimeoutDuration] = useState(60);
  const meetingEndedRef = useRef(false);
  const deviceControllerRef = useRef(null);

  const { sendMessage, receivedMessage } = useWebSocket();

  const httpService = useAxios(axiosConfig.envURL);

  const toggleMute = useCallback(async () => {
    if (!meetingSessionRef.current?.audioVideo) return;
    if (isMuted) {
      await meetingSessionRef.current.audioVideo.realtimeUnmuteLocalAudio();
    } else {
      await meetingSessionRef.current.audioVideo.realtimeMuteLocalAudio();
    }
    setIsMuted(!isMuted);
  }, [isMuted]);

  const toggleVideo = useCallback(async () => {
    if (!meetingSessionRef.current) return;
    const audioVideo = meetingSessionRef.current.audioVideo;
    if (!audioVideo) return;

    if (!hideVideo) {
      audioVideo.stopLocalVideoTile();
      setHideVideo(true);
    } else {
      audioVideo.startLocalVideoTile();
      setHideVideo(false);
    }
  }, [hideVideo]);

  useEffect(() => {
    let timer;
    // Timer only starts when call has actually started (both parties joined)
    if (callStarted) {
      timer = setInterval(() => {
        setCallDuration(prevDuration => prevDuration + 1);
      }, 1000);
    } else {
      clearInterval(timer);
      setCallDuration(0);
    }
    return () => clearInterval(timer);
  }, [callStarted]);

  const joinMeeting = useCallback(async () => {
    if (!meetingData || !meetingSessionRef.current?.audioVideo) return;
    const audioVideo = meetingSessionRef.current.audioVideo;
    if (!audioOnly && previewVideoRef.current) {
      audioVideo.stopVideoPreviewForVideoInput(previewVideoRef.current);
    }
    
    // Set audio output device now that we have a user gesture (user clicked "Start Call")
    try {
      const audioOutputDevices = await audioVideo.listAudioOutputDevices();
      if (audioOutputDevices.length > 0) {
        await audioVideo.chooseAudioOutput(audioOutputDevices[0].deviceId);
      }
    } catch (sinkErr) {
      // Silently handle NotAllowedError - browser will use default audio output
    }
    
    audioVideo.start();
    if (!audioOnly) {
      audioVideo.startLocalVideoTile();
    }

    sendMessage({
      type: 'staffHasJoined',
      meetingId: meetingIdRef.current,
      senderId: data.senderId,
      senderName: userFullName,
      recipientId: data.recipientId,
      isAudioOnly: audioOnly,
    });
    // Exit preview mode to show provider's video, but don't start timer yet
    setInPreviewMode(false);
    // Timer will start when patient joins (via meetingJoined WebSocket message)
  
  }, [meetingData, audioOnly, data, sendMessage, userFullName]);

  const leaveMeetingModal = () => {
    setShowLeaveModal(true)
  }

  const leaveMeeting = useCallback(async () => {
    meetingEndedRef.current = true;
    setCallStarted(false); // Reset call started state
    setHasRemoteAttendee(false); // Reset remote attendee state
    setPatientHasLeft(false); // Reset patient left state
    hasRemoteAttendeeRef.current = false; // Reset refs
    hadRemoteTilesRef.current = false;
    if (meetingSessionRef.current) {
      const audioVideo = meetingSessionRef.current.audioVideo;
      if (audioVideo) {
        if (!audioOnly && previewVideoRef.current) {
          try {
            audioVideo.stopVideoPreviewForVideoInput(previewVideoRef.current);
          } catch (e) {
            // Silently handle errors
          }
        }
        await audioVideo.stopLocalVideoTile();
        await audioVideo.stopVideoInput();
        await audioVideo.stopAudioInput();
        await audioVideo.stop();
        audioVideo.unbindAudioElement();
      }
      if (deviceControllerRef.current) {
        try {
          await deviceControllerRef.current.destroy();
        } catch (e) {
          // Silently handle errors
        }
        deviceControllerRef.current = null;
      }
      if (meetingIdRef.current) {
        try {
          await httpService.delete(`/meeting/${meetingIdRef.current}`);
          // Clean up any other resources or perform additional state cleanup here
        } catch (err) {
          // Silently handle errors
        }
      }
    }
    onClose();
    sendMessage({
      type: 'staffHasLeft',
      meetingId: meetingIdRef.current,
      senderId: data.senderId,
      recipientId: data.recipientId,
    });
    setMeetingData(null);
    meetingSessionRef.current = null;
  }, [httpService, onClose, data, sendMessage, dispatch]);

  useEffect(() => {
    let countdownTimer;
    // Countdown timer runs when waiting for patient (not in preview, call not started yet)
    if (isWaiting && !callStarted) {
      countdownTimer = setInterval(() => {
        setTimeoutDuration(prev => {
          // called recipient for 60 seconds, hang up and display unavailable message
          if (prev <= 0) { 
            clearInterval(countdownTimer);
            leaveMeeting();
            dispatch(
              showAlert({
                  header: "Staff Unavailable",
                  message: `${data.recipientName} is not available`,
                  type: 'warning',
                  dismissable: false
              }
            ));
            return prev;
          }
          return prev - 1;
        });
      }, 1000);
    } else {
      clearInterval(countdownTimer);
      setTimeoutDuration(60);
    }
    return () => clearInterval(countdownTimer);
  }, [isWaiting, callStarted, leaveMeeting, data, dispatch]);

  useEffect(() => {
    const startMeetingSetup = async () => {
      try {

        const postData = {
          title: data.title,
          appointmentId: data.appointmentId,
          senderId: data.senderId,
          recipientId: data.recipientId,
          recipientName: data.recipientName,
          senderName: data.senderName,
          audioOnly: audioOnly
        };

        if (data.meetingId) {
          postData.meetingId = data.meetingId;
        }

        const meetingResponse = await httpService.request({
          method: 'POST',
          headers: { "Content-Type": "application/json" },
          url: `/meeting/join`,
          data: JSON.stringify(postData),
        });

        if (meetingResponse.data.data.meeting && meetingResponse.data.data.attendee) {
          meetingIdRef.current = meetingResponse.data.data.meeting.MeetingId;
          meetingArnRef.current = meetingResponse.data.data.meeting.MeetingArn;
          setMeetingData({
            Meeting: meetingResponse.data.data.meeting,
            Attendee: meetingResponse.data.data.attendee,
          });
        }
      } catch (err) {
        console.error('Error setting up the meeting:', err);
      }
    };

    startMeetingSetup();
  }, [userId, data, httpService]);

  useEffect(() => {
    if (meetingData) {
      localAttendeeIdRef.current = meetingData.Attendee.AttendeeId;
    }
  }, [meetingData]);

  useEffect(() => {
    if (meetingIdRef.current) {
      dispatch(setCurrentMeetingId(meetingIdRef.current));
    }
  }, [meetingIdRef.current, dispatch]);

  // Listen for meetingJoined WebSocket message to detect when patient joins
  useEffect(() => {
    if (!receivedMessage || receivedMessage.type !== 'meetingJoined') return;
    if (!meetingIdRef.current) return; // Wait for meetingId to be set
    
    // Check if this meetingJoined message is for our meeting and indicates patient joined
    const message = receivedMessage;
    const isOurMeeting = message.meetingId === meetingIdRef.current;
    // Check if participantId matches recipientId (patient) or if recipientId in message matches our recipientId
    const isPatientJoined = (message.participantId && parseInt(message.participantId) === parseInt(data.recipientId)) ||
                            (message.recipientId && parseInt(message.recipientId) === parseInt(userId));
    
    if (isOurMeeting && isPatientJoined && !callStarted) {
      // Patient has joined - start the timer
      setCallStarted(true);
    }
  }, [receivedMessage, data.recipientId, callStarted, userId]);

  // Fallback: Also detect patient join/leave via remote video tiles (for video calls only)
  useEffect(() => {
    if (audioOnly) return; // Audio calls use attendee presence callback instead
    
    const remoteTiles = videoTiles.filter(tile => !tile.localTile);
    const hasRemoteTiles = remoteTiles.length > 0;
    
    // If we have remote tiles and call hasn't started yet, patient has joined
    if (hasRemoteTiles && !callStarted && !inPreviewMode && meetingSessionRef.current) {
      // Patient has joined (we can see their video) - start the timer
      setCallStarted(true);
      hadRemoteTilesRef.current = true;
    }
    
    // Track if we have ever had remote tiles in this session for potential future logic,
    // but do NOT infer "patient has left" from video tiles disappearing, since that can
    // also mean the camera was simply turned off.
    if (hasRemoteTiles) {
      hadRemoteTilesRef.current = true;
    }
  }, [videoTiles, callStarted, inPreviewMode, audioOnly]);

  useEffect(() => {
    if (!meetingData) return;

    const logger = new ConsoleLogger('MeetingLogs', LogLevel.INFO);
    const deviceController = new DefaultDeviceController(logger);
    deviceControllerRef.current = deviceController;
    const configuration = new MeetingSessionConfiguration(meetingData.Meeting, meetingData.Attendee);
    const meetingSession = new DefaultMeetingSession(configuration, logger, deviceController);

    meetingSessionRef.current = meetingSession;

    if (meetingSessionRef.current) {
      meetingSessionRef.current.audioVideo.transcriptionController.subscribeToTranscriptEvent((transcriptEvent) => {
        const results = transcriptEvent.results || [];
      });
    }

    const observer = {
      audioVideoDidStart: () => {
        meetingEndedRef.current = false;
      },
      audioVideoDidStop: () => {
        meetingEndedRef.current = true;
      },
      videoTileDidUpdate: tileState => {
        if (audioOnly) return;
        
        const isLocalTile = tileState.localTile;
        const boundAttendeeId = tileState.boundAttendeeId || (isLocalTile ? localAttendeeIdRef.current : null);
        
        if (!boundAttendeeId && !isLocalTile) return;

        setVideoTiles(prevTiles => {
          const tileIndex = prevTiles.findIndex(t => t.tileId === tileState.tileId);
          if (tileIndex !== -1) {
            const existingTile = prevTiles[tileIndex];
            if (isLocalTile && existingTile.ref?.current && meetingSessionRef.current?.audioVideo) {
              const audioVideo = meetingSessionRef.current.audioVideo;
              try {
                const tile = audioVideo.getVideoTile(tileState.tileId);
                if (tile) {
                  audioVideo.bindVideoElement(tileState.tileId, existingTile.ref.current);
                } else if (!tileState.boundAttendeeId || tileState.boundAttendeeId === '') {
                  // Only attempt recovery if meeting hasn't ended
                  if (!meetingEndedRef.current) {
                    if (!audioOnly) {
                      audioVideo.startLocalVideoTile();
                    }
                  }
                }
              } catch (err) {
                // Silently handle errors
              }
            }
            return prevTiles;
          }

          const newRef = React.createRef();
          return [...prevTiles, { tileId: tileState.tileId, ref: newRef, boundAttendeeId, localTile: isLocalTile }];
        });
      },
      videoTileWasRemoved: tileId => {
        if (audioOnly) return;
        setVideoTiles(prevTiles => {
          const tileToRemove = prevTiles.find(t => t.tileId === tileId);
          if (tileToRemove?.localTile) {
            return prevTiles;
          }
          return prevTiles.filter(tile => tile.tileId !== tileId);
        });
      },
    };

    const controllerObserver = {
      eventDidReceive(name, attributes) {
        if (name === 'meetingEnded') {
          meetingEndedRef.current = true;
          leaveMeeting();
        }
      }
    };

    // const activeSpeakerCallback = (attendeeIds) => {
    //   if (attendeeIds && attendeeIds.length > 0) {
    //     console.log("Laurel: setting attendee as active speaker: ", attendeeIds[0])
    //     setActiveSpeakerId(attendeeIds[0]); // Set the active speaker (other attendee)
    //   } else {
    //     console.log("Laurel: setting active speaker null: ")
    //     setActiveSpeakerId(null); // No active speaker
    //   }
    // };


    // Track attendee presence to detect when patient joins/leaves
    let attendeePresenceCallback = null;
    attendeePresenceCallback = (attendeeId, present) => {
      // Only track remote attendees (exclude local attendee)
      if (attendeeId === localAttendeeIdRef.current) return;
      
      if (present) {
        // Remote attendee (patient) has joined
        setHasRemoteAttendee(true);
        hasRemoteAttendeeRef.current = true;
        setPatientHasLeft(false); // Reset patient left state
        // Start the timer when patient joins
        setCallStarted(true);
      } else {
        // Patient has left - check if they were previously present
        if (hasRemoteAttendeeRef.current) {
          setPatientHasLeft(true);
        }
        setHasRemoteAttendee(false);
        hasRemoteAttendeeRef.current = false;
      }
    };
    meetingSession.audioVideo.realtimeSubscribeToAttendeeIdPresence(attendeePresenceCallback);

    meetingSession.audioVideo.addObserver(observer);
    meetingSession.eventController.addObserver(controllerObserver);
    // meetingSession.audioVideo.subscribeToActiveSpeakerDetector(
    //   new DefaultActiveSpeakerPolicy(),
    //   activeSpeakerCallback
    // );

    const setupDevices = async () => {
      // Invoke devices
      meetingSession.audioVideo.setDeviceLabelTrigger(async () => {
        const constraints = {
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true
          },
          video: !audioOnly
        };
        return await navigator.mediaDevices.getUserMedia(constraints);
      });


      // setup audio input
      const audioInputDevices = await deviceController.listAudioInputDevices();
      if (audioInputDevices.length > 0) {
        await deviceController.startAudioInput(audioInputDevices[0].deviceId);
        meetingSession.audioVideo.bindAudioElement(audioElementRef.current);
      } else {
        await meetingSession.audioVideo.chooseAudioInputDevice('default');
      }

      // set up video input
      if (!audioOnly) {
        const videoInputDevices = await deviceController.listVideoInputDevices();
        if (videoInputDevices.length > 0) {
          await deviceController.startVideoInput(videoInputDevices[0].deviceId);
          await meetingSession.audioVideo.startVideoPreviewForVideoInput(previewVideoRef.current);
          setIsVideoPreviewReady(true);
        }
      }

      // Note: chooseAudioOutput is deferred to joinMeeting() where user gesture is available
      // Calling it here would trigger "A user gesture is required" error in Safari
    };

    if (previewVideoRef.current || audioOnly) {
      setupDevices();
    }

    return () => {
      meetingSession.audioVideo.transcriptionController.unsubscribeFromTranscriptEvent()
      meetingSession.audioVideo.removeObserver(observer);
      meetingSession.eventController.removeObserver(controllerObserver);
      if (attendeePresenceCallback) {
        meetingSession.audioVideo.realtimeUnsubscribeToAttendeeIdPresence(attendeePresenceCallback);
      }
      if (deviceControllerRef.current) {
        deviceControllerRef.current.destroy().catch(() => {});
        deviceControllerRef.current = null;
      }
    };
  }, [meetingData, audioOnly]);

  useEffect(() => {
    if (!meetingSessionRef.current?.audioVideo) return;
    // Don't attempt recovery if meeting has ended
    if (meetingEndedRef.current) {
      return;
    }
    videoTiles.forEach(tile => {
      if (tile.ref?.current) {
        const audioVideo = meetingSessionRef.current.audioVideo;
        try {
          const chimeTile = audioVideo.getVideoTile(tile.tileId);
          if (chimeTile) {
            audioVideo.bindVideoElement(tile.tileId, tile.ref.current);
          } else {
            if (tile.localTile && !audioOnly && !meetingEndedRef.current) {
              audioVideo.startLocalVideoTile();
            }
          }
        } catch (err) {
          // Silently handle errors
        }
      }
    });
  }, [videoTiles, audioOnly]);

  const recipientVideoOff = videoTiles.length === 1 && remoteTiles.length === 0 && !inPreviewMode && callStarted;

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes < 10 ? '0' : ''}${minutes}:${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}`;
  };

  const toggleModalSize = () => {
    dispatch(setExpanded());
  };

  const toggleMinimizeSize = () => {
    dispatch(setMinimized());
  };

  const getInitials = (name) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase();
  };

  // const otherAttendeeId = Array.from(attendeesRef.current).find(
  //   (attendeeId) => attendeeId !== localAttendeeIdRef.current
  // );

  return (
     <div className={minimized ? styles.VideoCallContainerMinimized : expanded ? styles.VideoCallContainerExpanded : styles.VideoCallContainer}>
      <div className={styles.VideoCallTopSection}>
        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', columnGap:"10px" }}>
          <Button variant="white" className={styles.expandIcon} onClick={toggleMinimizeSize}>
            <MinimizeIcon />
          </Button>
          <Button variant="white" className={styles.expandIcon} onClick={toggleModalSize}>
            <ExpandIcon />
          </Button>
          <CloseButton onClick={leaveMeeting} className={styles.closeButton} />
        </div>
      </div>
      <div className={styles.VideoCallMiddleSection}>
        <div style={{ width: '100%', height: '100%', position:"relative"}}>
          {audioOnly ? (
                <div style={{display:"flex", flexDirection:"column", alignItems:"center", height:"100%", justifyContent:"center"}}>
                  <div className={`${styles.iconContainer} ${styles.icon}`} style={{marginBottom: "20px"}}>
                    <UserProfilePictureIcon width="88px" height="88px" />
                    <div className={styles.initialsCall}>{data.recipientName?.split(' ')[0][0] + data.recipientName?.split(' ')[1][0]}</div>
                  </div>
                  {inPreviewMode ? (
                      <div className={styles.callingText}>{`Ready to call ${data.recipientName}?`}</div>
                  ) : 
                    <div style={{display: "flex", flexDirection: "column", alignItems: "center", gap: "10px"}}>
                      <span className="headline-5">{data.recipientName}</span>
                      {patientHasLeft && 
                        <div className={styles.callingText}>{data.title?.toLowerCase().includes('staff') ? 'Staff member' : 'Patient'} has left.</div>
                      }
                      {isWaiting && !patientHasLeft && 
                        <div className={styles.callingText}>Calling ...</div>
                      }
                    </div>
                  }
                </div>
          ) : (
            inPreviewMode ? (
              <div style={{ width: "100%", height: "100%", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center" }}>
                <span className="subtitle-1">{`Ready to ${joiningStaffCall ? "join call with " : "call"} ${data.recipientName}`}</span>
                <span className="body-2">Please ensure your camera and microphone are enabled.</span>
                <video ref={previewVideoRef} autoPlay playsInline style={{ width: '100%', height: '100%', borderRadius: "12px" }} />
              </div>
            ) : (
              <div className={styles.videoArea}>
                {videoTiles.map(tile => (
                  <div key={tile.tileId} className={tile.boundAttendeeId === localAttendeeIdRef.current ? (expanded ? styles.localVideoTopRightExpanded : styles.localVideoTopRight) : styles.remoteVideo}>
                    <video
                      ref={tile.ref}
                      className={`${styles.videoElement} ${tile.boundAttendeeId === localAttendeeIdRef.current ? styles.videoElementLocal : ""}`}
                    />
                  </div>
                ))}
                {recipientVideoOff && (
                  <div className={styles.waitingMessage}>
                    <div className={styles.initialsPlaceholderTest}>
                        <UserProfilePictureIcon width="88px" height="88px" />
                        <div className={styles.initialsTest}>
                          {getInitials(data.recipientName)}
                        </div>
                      </div>
                    {patientHasLeft && (
                      <div style={{fontSize:"20px", marginTop: "20px"}}>{data.title?.toLowerCase().includes('staff') ? 'Staff member' : 'Patient'} has left.</div>
                    )}
                    {isWaiting && !patientHasLeft && (
                      <>
                        <div style={{fontSize:"20px", marginTop: "20px"}}>Calling ...</div>
                        <div style={{marginTop: "10px"}}>{data.recipientName}</div>
                      </>
                    )}
                  </div>
                )}
                {!recipientVideoOff && patientHasLeft && (
                  <div className={styles.waitingMessage}>
                    <div style={{fontSize:"20px"}}>{data.title?.toLowerCase().includes('staff') ? 'Staff member' : 'Patient'} has left.</div>
                  </div>
                )}
                {!recipientVideoOff && isWaiting && !patientHasLeft && (
                  <div className={styles.waitingMessage}>
                    <div style={{fontSize:"20px"}}>Calling ...</div>
                    <div>{data.recipientName}</div>
                  </div>
                )}
              </div>
            )
          )}
        </div>
        <audio ref={audioElementRef} style={{ display: 'none' }}></audio>
      </div>
      <div className={styles.VideoCallButtonSection} style={inPreviewMode ? {height:"110px"} : {height:"50px"}}>
        {!inPreviewMode && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
            <div style={{ width: "25%" }}></div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", width: "40%" }}>
              {!audioOnly &&
                <Button className={styles.muteButton} variant="light" onClick={toggleVideo}>
                  {hideVideo ? (
                    <FontAwesomeIcon style={{ color: "#4F4F4F", }} icon={['fas', 'video-slash']} size="xl" />
                  ) : (
                    <FontAwesomeIcon style={{ color: "#4F4F4F", }} icon={['fas', 'video']} size="xl" />
                  )}
                </Button>
              }
              <Button variant="light" onClick={toggleMute} style={{ width: "51px", marginRight: "5px" }} className={styles.muteButton}>
                {isMuted ? <FontAwesomeIcon icon="fa-solid fa-microphone-slash" style={{ color: "#4F4F4F", }} size="xl" /> : <FontAwesomeIcon icon="fa-solid fa-microphone" style={{ color: "#4F4F4F", }} size="xl" />}
              </Button>
              <Button variant="danger" onClick={()=>patientHasLeft ? leaveMeeting() : setShowLeaveModal(true)} className={styles.endCallButton}><CallEndIcon /></Button>
            </div>
            <div style={{ width: "25%", textAlign: 'right' }}>
              {callStarted &&
                <span className="subtitle-1">{formatTime(callDuration)}</span>
              }
            </div>
          </div>
        )}
        {inPreviewMode && (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: "column", gap: "20px", paddingTop: "20px" }}>
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <Form style={{ display: "flex", alignItems: "center" }}>
                <Form.Check
                  type="switch"
                  id="custom-switch"
                  checked={!isMuted}
                  onChange={toggleMute}
                />
                {isMuted ? (
                  <FontAwesomeIcon icon="fa-solid fa-microphone-slash" style={{ color: "#4F4F4F", }} size="lg" />
                ) : (
                  <FontAwesomeIcon icon="fa-solid fa-microphone" style={{ color: "#4F4F4F", }} size="lg" />
                )}
              </Form>
            </div>
            <div style={{ width: "85%", display: 'flex', justifyContent: 'center', alignItems: 'center', columnGap: "20px" }}>
              <Button 
                className={styles.StartCallButton} 
                onClick={joinMeeting}
                disabled={!audioOnly ? !isVideoPreviewReady : false}
              >

                {(!audioOnly && !isVideoPreviewReady) && 
                  <Spinner className="spinner" animation="border" role="status" variant="secondary" />
                }
                {joiningStaffCall ? "Join Call" : "Start Call"}
              </Button>
              <Button className={styles.CancelCallButton} onClick={leaveMeeting}>Cancel</Button>
            </div>
          </div>
        )}
      </div>
      <Modal show={showLeaveModal} onHide={()=>setShowLeaveModal(false)}>
          <Modal.Header closeButton>
              <Modal.Title>End Meeting</Modal.Title>
          </Modal.Header>
          <Modal.Body>
               <div>Are you sure you want to end the call?</div>
            </Modal.Body>
            <Modal.Footer>
               
            <Button 

                        className={styles.SecondaryButton}
                        onClick={()=>setShowLeaveModal(false)}
                    >
                        Cancel
                    </Button>
                    <Button 
                        className={styles.ModelButton}
                        onClick={leaveMeeting}
                    >
                        Confirm
                    </Button>
            </Modal.Footer>
      </Modal>
    </div>
  );

}

export default VideoCallContainer;
